/**
 * Drag the mobile navigation drawer toward its edge to dismiss it.
 *
 * The drawer already closes from the backdrop and the back button. What was
 * missing is what a thumb expects from a panel that slid in: being able to send
 * it back the way it came. The drag *is* the entry animation, only driven by
 * the finger instead of the clock.
 *
 * Everything with physics in it — the 8px deadzone, axis lock, fling detection,
 * rubber-band, the haptic pulse at the commit threshold — comes from
 * `lib/gestures`. None of that is re-implemented here; this hook only decides
 * what those mean for a panel anchored to the left edge. The DOM binding is
 * `pointerHandlers` (Pointer Events + pointer capture), matching the other
 * swipe hooks in this repo (`use-row-delete-swipe.ts`), not a Touch+Mouse
 * `attach`-by-ref adapter.
 *
 * Two deliberate asymmetries:
 *
 *   - **Only leftward counts.** There is nothing to reveal on the right, so
 *     that direction gets a short capped rubber-band (it acknowledges the
 *     finger) and never commits: `direction === "left"` is checked, not just
 *     `committed`. Without that check a fast right flick would close the
 *     drawer — the opposite of what the gesture asks for.
 *   - **The axis is plain `"x"`.** A vertical drag aborts the gesture instead
 *     of competing with it, so the drawer's navigation keeps scrolling
 *     normally. The container's `touch-action: pan-y` is the other half of that
 *     deal: the browser keeps Y and yields X.
 *
 * `prefers-reduced-motion` is deliberately not consulted here: a drag is not
 * animation, it is a direct response to the finger. The part that *is*
 * animation — the settle on release — is collapsed by the global guard in
 * `global.css`.
 */

import { createSignal } from "solid-js";

import { isEditableTarget } from "@/lib/gestures/guards";
import { NAV_DRAWER_DISMISS } from "@/lib/gestures/presets";
import { applyRubberBand, computeCommit, createGestureRecognizer } from "@/lib/gestures/recognizer";
import { pointerHandlers } from "@/lib/gestures/solid";

const {
  thresholdPx: THRESHOLD_PX,
  commitThresholdPx: COMMIT_THRESHOLD_PX,
  rubberBandFactor: RUBBER_BAND_FACTOR,
  rubberBandLimitPx: RUBBER_BAND_LIMIT_PX,
  hapticMs: HAPTIC_MS,
  fallbackWidthPx: FALLBACK_WIDTH_PX,
  fling: DRAWER_FLING,
} = NAV_DRAWER_DISMISS;

interface NavDrawerSwipeOptions {
  /** The drawer is open (closed, the gesture never even starts).*/
  isOpen: () => boolean;
  /** Close it. Called once, when the drag commits.*/
  close: () => void;
  /** The drawer panel; its measured width calibrates the backdrop fade.*/
  panel?: () => HTMLElement | undefined;
}

export interface NavDrawerSwipe {
  /** Current panel offset in px (≤ 0 except for the rubber-band).*/
  dragPx: () => number;
  /** A drag is in flight: the CSS transition must be off.*/
  isDragging: () => boolean;
  /** Backdrop opacity while dragging (1 at rest).*/
  backdropOpacity: () => number;
  /** Pointer handlers for the element that owns the gesture (the drawer box).*/
  onPointerDown: (e: PointerEvent) => void;
  onPointerMove: (e: PointerEvent) => void;
  onPointerUp: (e: PointerEvent) => void;
  onPointerCancel: (e: PointerEvent) => void;
}

/** Panel offset for a given finger delta.*/
function panelOffsetFor(dx: number): number {
  if (dx <= 0) {
    // Toward the edge: 1:1 with the finger. Any smoothing here reads as lag,
    // because the panel is exactly what the finger believes it is touching.
    return dx;
  }
  // Inward: rubber-band (dx * factor) plus a hard cap, so the panel never
  // detaches from its edge no matter how hard the drag insists.
  return Math.min(applyRubberBand(dx, 0, RUBBER_BAND_FACTOR), RUBBER_BAND_LIMIT_PX);
}

export function useNavDrawerSwipe(options: NavDrawerSwipeOptions): NavDrawerSwipe {
  const [dragPx, setDragPx] = createSignal(0);
  const [isDragging, setIsDragging] = createSignal(false);
  // Measured at the start of each gesture: the width changes across rotations.
  // The type is explicit because the preset is `as const` and the initial value
  // would otherwise pin the literal type `280`.
  let widthPx: number = FALLBACK_WIDTH_PX;

  const recognizer = createGestureRecognizer({
    axis: "x",
    thresholdPx: THRESHOLD_PX,
    fling: DRAWER_FLING,
    haptics: true,
    hapticMs: HAPTIC_MS,
    commitPx: COMMIT_THRESHOLD_PX,
    canStart: ({ target }) => {
      if (!options.isOpen()) {
        return false;
      }
      // A text field inside the drawer keeps the drag: there, sliding selects
      // text or moves the caret — it does not dismiss.
      return !isEditableTarget(target);
    },
    onStart: () => {
      // `offsetWidth` is 0 for an unmeasured/hidden panel, hence the explicit
      // `> 0` rather than `??`: zero is a missing measurement, not a width.
      const measured = options.panel?.()?.offsetWidth;
      widthPx = measured !== undefined && measured > 0 ? measured : FALLBACK_WIDTH_PX;
      setDragPx(0);
      setIsDragging(true);
    },
    onMove: (sample) => {
      // Dragging over text would select it underneath the gesture.
      globalThis.getSelection()?.removeAllRanges();
      setDragPx(panelOffsetFor(sample.dx));
    },
    onEnd: (sample) => {
      const { committed, direction } = computeCommit(sample, { commitPx: COMMIT_THRESHOLD_PX });
      // Release the drag BEFORE closing, so the panel travels from wherever the
      // finger left it to off-screen with the transition, instead of jumping.
      setIsDragging(false);
      setDragPx(0);
      if (committed && direction === "left") {
        options.close();
      }
    },
    onCancel: () => {
      setIsDragging(false);
      setDragPx(0);
    },
  });

  const backdropOpacity = () => {
    if (!isDragging()) {
      return 1;
    }
    const travelled = Math.abs(Math.min(dragPx(), 0));
    return Math.max(0, 1 - travelled / widthPx);
  };

  const base = pointerHandlers(recognizer);

  return {
    dragPx,
    isDragging,
    backdropOpacity,
    onPointerDown: base.onPointerDown,
    onPointerMove: base.onPointerMove,
    onPointerUp: base.onPointerUp,
    onPointerCancel: base.onPointerCancel,
  };
}
